/**
 * campsAlongApi.ts — fetch the campsites near a point or along a line from the
 * public bounds-search (no sign-in, no rate-limited route geocoding).
 *
 * Each box is one bounds-search page; results are de-duplicated by id and the
 * corridor filter (campsAlong.campsAlongLine) trims them to the drive. Boxes
 * are cached for the session so re-opening a leg or re-rendering a night card
 * doesn't refetch.
 */

import { searchEntitiesByBounds } from '../lib/searchEntities';
import type { Entity } from '../types';
import { corridorBoxes, splitBox, thinLine, type BoundsBox, type LatLng } from './campsAlong';

const cache = new Map<string, Promise<Entity[]>>();
const MAX_CACHE = 120;

const boxKey = (b: BoundsBox) =>
  [b.north, b.south, b.east, b.west].map((n) => n.toFixed(2)).join(',');

function fetchBox(b: BoundsBox): Promise<Entity[]> {
  const key = boxKey(b);
  const hit = cache.get(key);
  if (hit) return hit;
  const p = searchEntitiesByBounds({ bounds: b, zoom: 10, limit: 500 })
    .then((r) => {
      // The endpoint can answer 200 with an error body (e.g. a box over a
      // page full) — treat that as a failure so the box gets split.
      if (r.error) throw new Error(r.error);
      return r.entities || [];
    })
    .catch((err) => {
      cache.delete(key); // let a retry go to the network
      throw err;
    });
  if (cache.size >= MAX_CACHE) {
    const first = cache.keys().next().value;
    if (first !== undefined) cache.delete(first);
  }
  cache.set(key, p);
  return p;
}

/** One box; if the endpoint refuses it (too many listings), its quarters. */
async function fetchBoxSplitting(b: BoundsBox, depth = 0): Promise<Entity[]> {
  try {
    return await fetchBox(b);
  } catch (err) {
    if (depth >= 2) throw err;
    const parts = await Promise.all(splitBox(b).map((q) => fetchBoxSplitting(q, depth + 1)));
    return dedupe(parts);
  }
}

/** Run `fn` over `items` with at most `limit` in flight. */
async function pool<T, R>(items: T[], limit: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return out;
}

function dedupe(lists: Entity[][]): Entity[] {
  const seen = new Set<number>();
  const out: Entity[] = [];
  for (const list of lists) {
    for (const e of list) {
      const id = Number((e as { id?: number; entity_id?: number }).id ?? (e as { entity_id?: number }).entity_id);
      if (!Number.isFinite(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
  }
  return out;
}

/** Listings within a square of ±radiusKm around a point. */
export function fetchEntitiesNear(point: LatLng, radiusKm: number): Promise<Entity[]> {
  return fetchEntitiesAlong([point], radiusKm);
}

/** Listings in boxes covering `line` ± padKm. Fails only if every box fails. */
export async function fetchEntitiesAlong(line: LatLng[], padKm: number): Promise<Entity[]> {
  const boxes = corridorBoxes(thinLine(line), padKm);
  if (!boxes.length) return [];
  let failures = 0;
  const lists = await pool(boxes, 4, (b) =>
    fetchBoxSplitting(b).catch(() => {
      failures += 1;
      return [] as Entity[];
    })
  );
  if (failures === boxes.length) throw new Error('Couldn’t reach the campsite search');
  return dedupe(lists);
}
